import { useState }           from 'react';
import { PaperDoll }          from '../molecules/PaperDoll';
import { StatsList }          from '../molecules/StatsList';
import { InventoryGrid }      from '../molecules/InventoryGrid';
import { RuneInventoryGrid }  from '../molecules/RuneInventoryGrid';
import { RuneWordModal }      from '../molecules/RuneWordModal';
import { SetBonusPanel }      from '../molecules/SetBonusPanel';

/**
 * Full-page character sheet accessible from the idle screen.
 * Shows the paper-doll equipment layout, the player's effective stats,
 * and the inventory below.
 *
 * A toggle above the doll switches between:
 *   ⚔️ Items — normal equipment slots + item inventory
 *   🔮 Runes — per-slot rune sockets + stacked rune inventory
 *
 * In Runes view an ⓘ button opens the Rune Words reference modal.
 */
export function CharacterScreen({ onBack, inventory, equipped, onEquip, onUnequip, onSell, onSellAll, onSellAllRunes, onLock, onEquipRuneWord, onUnequipAllRunes, totalGold, playerLevel = 1, activatedTalents = [] }) {
  const [dollView,      setDollView]      = useState('items');
  const [runeWordModal, setRuneWordModal] = useState(false);

  return (
    <div className="character-screen">
      <button className="back-btn" onClick={onBack}>← Back</button>

      <h1 className="character-screen-title">Character</h1>

      {/* ── Persistent player level badge ── */}
      <div className="player-level-badge">
        <span className="player-level-num">{playerLevel}</span>
        <span className="player-level-label">Player Level</span>
      </div>

      <div className="character-layout">
        <div className="doll-column">
          {/* ── Items / Runes toggle ── */}
          <div className="doll-view-toggle">
            <button
              className={`doll-toggle-btn${dollView === 'items' ? ' doll-toggle-btn--active' : ''}`}
              onClick={() => setDollView('items')}
            >
              ⚔️ Items
            </button>
            <button
              className={`doll-toggle-btn${dollView === 'runes' ? ' doll-toggle-btn--active' : ''}`}
              onClick={() => setDollView('runes')}
            >
              🔮 Runes
            </button>
            {dollView === 'runes' && (
              <>
                <button
                  className="rw-info-btn"
                  title="View all Rune Words"
                  onClick={() => setRuneWordModal(true)}
                >
                  ⬡ Rune Words
                </button>
                <button
                  className="rw-info-btn rw-unequip-all-btn"
                  title="Move all socketed runes back to inventory"
                  onClick={onUnequipAllRunes}
                >
                  ✕ Unequip All
                </button>
              </>
            )}
          </div>

          <PaperDoll equipped={equipped} onUnequip={onUnequip} view={dollView} />
        </div>

        <StatsList equipped={equipped} activatedTalents={activatedTalents} />
        <SetBonusPanel equipped={equipped} />
      </div>

      {/* ── Inventory section: items or runes based on active toggle ── */}
      {dollView === 'runes' ? (
        <RuneInventoryGrid
          inventory={inventory}
          onEquip={onEquip}
          onSell={onSell}
          onSellAllRunes={onSellAllRunes}
          totalGold={totalGold}
          playerLevel={playerLevel}
        />
      ) : (
        <InventoryGrid
          inventory={inventory}
          onEquip={onEquip}
          onSell={onSell}
          onSellAll={onSellAll}
          onLock={onLock}
          totalGold={totalGold}
          playerLevel={playerLevel}
          equipped={equipped}
        />
      )}

      {/* ── Rune Words modal ── */}
      {runeWordModal && (
        <RuneWordModal
          equipped={equipped}
          inventory={inventory}
          onEquipRuneWord={onEquipRuneWord}
          onClose={() => setRuneWordModal(false)}
        />
      )}
    </div>
  );
}
